import React, { useState, useEffect, useMemo } from 'react';
import { getHandlerRequestsByHandlerId } from '../api/actions';
import { useParticipants } from '../hooks/useParticipants';
import { getAdminRotationRequests, assignHandlerToRotation, updateParticipantAsAdmin, createParticipantAsAdmin, DeleteParticipant } from '../api/actions';
import { getLoggedInParticipant } from '../api/participantAuth';
import AdminAssignHandlerModal from './AdminAssignHandlerModal';

export default function AdminTab({ 
  participant, 
  onMessage, 
  onParticipantUpdate, 
  isEditing, 
  onEditingChange, 
  saving, 
  setSaving 
}) {
  const [adminRotationRequests, setAdminRotationRequests] = useState([]);
  // State for handler requests received for selected participant
  const [adminHandlersRequestsReceived, setAdminHandlersRequestsReceived] = useState([]);
  const [loadingRequests, setLoadingRequests] = useState(false);
  const [showAssignHandlerModal, setShowAssignHandlerModal] = useState(false);
  const [selectedRotationRequest, setSelectedRotationRequest] = useState(null);
  const [selectedParticipantId, setSelectedParticipantId] = useState('');
  const [isNewParticipant, setIsNewParticipant] = useState(false);
  const [participantFormData, setParticipantFormData] = useState({});
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingParticipant, setDeletingParticipant] = useState(false);
  const [deleteResult, setDeleteResult] = useState(null);
  const { participants: allParticipants, loadParticipants } = useParticipants();

  const hasRole = (roleToCheck) => {
    if (Array.isArray(participant?.role)) {
      return participant.role.some(r => r.toLowerCase() === roleToCheck.toLowerCase());
    }
    return participant?.role?.toLowerCase() === roleToCheck.toLowerCase();
  };


  // Calculate approved birds and open slots for handler
  const approvedBirds = useMemo(() => {
    return adminHandlersRequestsReceived
      .filter(req => req.status && req.status.toLowerCase() === 'approved')
      .reduce((sum, req) => sum + (parseInt(req.numberOfBirds || req.birdsRequested) || 0), 0);
  }, [adminHandlersRequestsReceived]);

  const openSlots = (participantFormData.handlerInfo?.totalBirds || 0) - approvedBirds;

  // Load admin rotation requests
  const loadAdminRotationRequests = async () => {
    if (!hasRole('admin')) {
      return;
    }
    
    try {
      setLoadingRequests(true);
      const result = await getAdminRotationRequests();
      
      if (result.success) {
        setAdminRotationRequests(result.requests || []);
        onMessage(`Loaded ${result.requests?.length || 0} admin rotation requests`, 'success');
      } else {
        onMessage(`Failed to load rotation requests: ${result.message}`, 'error');
      }
    } catch (error) {
      onMessage('Error loading rotation requests', 'error');
    } finally {
      setLoadingRequests(false);
    }
  };

  useEffect(() => {
    if (hasRole('admin')) {
      loadAdminRotationRequests();
    }
  }, [participant]);

  // Global save integration
  useEffect(() => {
    window.adminTabSave = async () => {
      try {
        setSaving(true);
        // Admin tab doesn't have traditional save operations like other tabs
        // But we can refresh data to ensure it's current
        await loadAdminRotationRequests();
        onMessage('Admin data refreshed successfully');
      } catch (error) {
        onMessage('Error refreshing admin data');
        throw error;
      } finally {
        setSaving(false);
        if (onEditingChange) {
          onEditingChange(false);
        }
      }
    };

    return () => {
      delete window.adminTabSave;
    };
  }, [setSaving, onEditingChange, onMessage]);

  // Filter pending rotation requests and enrich with breeder information
  const pendingRotationRequests = useMemo(() => {
    const pending = adminRotationRequests.filter(r => r.status === 'pending');
    
    // Enrich with participant data
    return pending.map(request => {
      const breeder = allParticipants?.find(p => p.id === request.breederId);
      
      if (breeder) {
        return {
          ...request,
          breederName: breeder.fullName,
          breederLoftName: breeder.loftName,
          breederEmail: breeder.emailAddress,
          breederMobile: breeder.mobile
        };
      }
      
      return request;
    });
  }, [adminRotationRequests, allParticipants]);

  // Get available handlers for assignment
  const availableHandlers = useMemo(() => {
    if (!allParticipants) return [];
    
    const handlers = allParticipants.filter(p => {
      const hasHandlerRole = Array.isArray(p.role) 
        ? p.role.some(r => r.toLowerCase() === 'handler')
        : p.role?.toLowerCase() === 'handler';
      
      // Include all handlers, not just those with openSlots > 0
      // Let the modal show capacity details and let admin decide
      return hasHandlerRole;
    });
    
    return handlers;
  }, [allParticipants]);

  // Initialize participant form
  const initializeParticipantForm = (participant = null) => {
    if (participant) {
      // Editing existing participant - populate all current data
      const formData = {
        id: participant.id,
        fullName: participant.fullName || '',
        loftName: participant.loftName || '',
        emailAddress: participant.emailAddress || '',
        mobile: participant.mobile || '',
        role: Array.isArray(participant.role) ? [...participant.role] : (participant.role ? [participant.role] : ['breeder']),
        hideContactInfo: participant.hideContactInfo || false,
        preferredNotificationMethod: participant.preferredNotificationMethod || ['email'],
        handlerInfo: {
          section: participant.handlerInfo?.section || '',
          distance: participant.handlerInfo?.distanceFromRaceStation || participant.handlerInfo?.distance || 0,
          totalBirds: participant.handlerInfo?.totalBirds || 0,
          openSlots: participant.handlerInfo?.openSlots || 0,
          reserved: participant.handlerInfo?.reserved || 0,
          breederApplicationMethod: participant.handlerInfo?.breederApplicationMethod || ['in-app']
        },
        breederInfo: {
          totalBirdsForRace: participant.breederInfo?.totalBirdsForRace || 0,
          handlerName: participant.breederInfo?.handlerName || null,
          assignedBirds: participant.breederInfo?.assignedBirds || []
        }
      };
      setParticipantFormData(formData);
    } else {
      // New participant - start with defaults
      const newFormData = {
        fullName: '',
        loftName: '',
        emailAddress: '',
        mobile: '',
        role: ['breeder'],
        hideContactInfo: false,
        preferredNotificationMethod: ['email'],
        handlerInfo: {
          section: '',
          distance: 0,
          totalBirds: 0,
          openSlots: 0,
          reserved: 0,
          breederApplicationMethod: ['in-app']
        },
        breederInfo: {
          totalBirdsForRace: 0,
          handlerName: null,
          assignedBirds: []
        }
      };
      setParticipantFormData(newFormData);
    }
  };

  // Handle participant selection
  const handleParticipantSelect = async (participantId) => {
    setSelectedParticipantId(participantId);

    if (participantId === 'new') {
      setIsNewParticipant(true);
      initializeParticipantForm(null);
      setAdminHandlersRequestsReceived([]);
    } else if (participantId) {
      setIsNewParticipant(false);
      const participant = allParticipants?.find(p => p.id === participantId);
      if (participant) {
        initializeParticipantForm(participant);
        // If participant is a handler, fetch their requests
        const hasHandlerRole = Array.isArray(participant.role)
          ? participant.role.some(r => r.toLowerCase() === 'handler')
          : participant.role?.toLowerCase() === 'handler';
        if (hasHandlerRole) {
          try {
            const result = await getHandlerRequestsByHandlerId(participant.id);
            if (result.success) {
              setAdminHandlersRequestsReceived(result.requests || []);
            } else {
              setAdminHandlersRequestsReceived([]);
            }
          } catch (error) {
            setAdminHandlersRequestsReceived([]);
          }
        } else {
          setAdminHandlersRequestsReceived([]);
        }
      } else {
        setAdminHandlersRequestsReceived([]);
      }
    } else {
      // Clear selection
      setIsNewParticipant(false);
      setParticipantFormData({});
      setAdminHandlersRequestsReceived([]);
    }
  };

  // Update form field
  const updateFormField = (path, value) => {
    setParticipantFormData(prev => {
      const keys = path.split('.');
      const updated = { ...prev };
      let current = updated;
      
      for (let i = 0; i < keys.length - 1; i++) {
        current[keys[i]] = { ...current[keys[i]] };
        current = current[keys[i]];
      }
      
      current[keys[keys.length - 1]] = value;
      return updated;
    });
  };

  // Save participant
  const saveParticipant = async () => {
    if (
      !participantFormData.fullName ||
      (!participantFormData.emailAddress && !participantFormData.mobile)
    ) {
      onMessage('Please fill in required fields (Name and either Email or Mobile)', 'error');
      return;
    }

    try {
      setSaving(true);
      
      let result;
      if (isNewParticipant) {
        result = await createParticipantAsAdmin(participantFormData);
      } else {
        if (!participantFormData.id) {
          onMessage('❌ Error: Participant ID is missing for update', 'error');
          return;
        }
        result = await updateParticipantAsAdmin(participantFormData.id, participantFormData);
      }
      
      if (result.success) {
        const successMessage = isNewParticipant 
          ? `✅ Participant "${participantFormData.fullName}" was successfully added to the system!`
          : `✅ Participant "${participantFormData.fullName}" was successfully updated!`;
        
        onMessage(successMessage, 'success');
        
        // Reload participants first, then reset form
        if (onParticipantUpdate) {
          await onParticipantUpdate();
        }
        
        // Reset form after reload
        setSelectedParticipantId('');
        setIsNewParticipant(false);
        setParticipantFormData({});
      } else {
        onMessage(`❌ Failed to ${isNewParticipant ? 'add' : 'update'} participant: ${result.message}`, 'error');
      }
    } catch (error) {
      onMessage('An error occurred while saving participant', 'error');
    } finally {
      setSaving(false);
    }
  };

  // Delete participant
  const deleteParticipant = async () => {
    if (!participantFormData.id) {
      onMessage('❌ Error: No participant selected for deletion', 'error');
      return;
    }

    try {
      setDeletingParticipant(true);
      setDeleteResult(null);
      
      // Get the current logged in participant's auth token
      const authData = getLoggedInParticipant();
      if (!authData || !authData.authToken) {
        setDeleteResult({ success: false, message: 'Authentication required. Please log in again.' });
        return;
      }
      
      
      const result = await DeleteParticipant(participantFormData.id);
      
      if (result.success) {
        setDeleteResult({ success: true, message: `Participant "${participantFormData.fullName}" was successfully deleted!` });
        
        // Reload participants first, then reset form
        if (onParticipantUpdate) {
          await onParticipantUpdate();
        }
        
        // Also force reload from hook to ensure fresh data
        await loadParticipants(true);
        
        // Reset form after deletion
        setSelectedParticipantId('');
        setIsNewParticipant(false);
        setParticipantFormData({});
      } else {
        setDeleteResult({ success: false, message: `Failed to delete participant: ${result.message}` });
      }
    } catch (error) {
      let errorMessage = 'An error occurred while deleting participant';
      
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data) {
        errorMessage = typeof error.response.data === 'string' ? error.response.data : JSON.stringify(error.response.data);
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setDeleteResult({ success: false, message: errorMessage });
    } finally {
      setDeletingParticipant(false);
    }
  };

  // Close delete modal and reset states
  const closeDeleteModal = () => {
    setShowDeleteConfirm(false);
    setDeleteResult(null);
    setDeletingParticipant(false);
  };

  // Handle assignment
  const handleAssignHandler = async ({ requestId, handlerId, birdsToAssign }) => {
    try {
      setSaving(true);
      const result = await assignHandlerToRotation(requestId, handlerId, birdsToAssign);
      
      if (result.success) {
        onMessage(`Successfully assigned ${birdsToAssign} birds to handler. ${result.remainingBirds || 0} birds remaining.`);
        
        // Reload rotation requests and participants
        await loadAdminRotationRequests();
        if (onParticipantUpdate) {
          await onParticipantUpdate();
        }
        
        setShowAssignHandlerModal(false);
        setSelectedRotationRequest(null);
      } else {
        onMessage(`Failed to assign handler: ${result.message}`);
      }
    } catch (error) {
      onMessage('An error occurred while assigning handler');
    } finally {
      setSaving(false);
    }
  };

  if (!hasRole('admin')) {
    return (
      <div className="alert alert-info">
        <i className="bi bi-info-circle me-2"></i>
        You need to have the Admin role to access this section. Admin roles are assigned by system administrators.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Admin Rotation Requests */}
      <div className="card shadow-sm">
        <div className="card-header bg-white py-3 d-flex justify-content-between align-items-center">
          <h5 className="mb-0" style={{ color: '#0b3d91' }}>
            <i className="bi bi-arrow-repeat me-2"></i>
            Admin Rotation Requests ({pendingRotationRequests.length})
          </h5>
          <button 
            className="btn btn-outline-primary btn-sm"
            onClick={loadAdminRotationRequests}
            disabled={loadingRequests}
          >
            <i className={`bi ${loadingRequests ? 'bi-arrow-clockwise' : 'bi-arrow-clockwise'} me-1`}></i>
            {loadingRequests ? 'Loading...' : 'Refresh'}
          </button>
        </div>
        <div className="card-body">
          {isEditing && (
            <div className="alert alert-warning d-flex align-items-center mb-3">
              <i className="bi bi-pencil-square me-2"></i>
              <strong>Edit Mode Active</strong> - You can assign handlers to rotation requests
            </div>
          )}

          {loadingRequests ? (
            <div className="text-center py-3">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-2 text-muted">Loading rotation requests...</p>
            </div>
          ) : pendingRotationRequests.length === 0 ? (
            <div className="text-center text-muted py-4">
              <i className="bi bi-inbox display-4 text-muted"></i>
              <p className="mt-2">No pending rotation requests</p>
              <small>Breeders who request "Admin Rotation" will appear here</small>
            </div>
          ) : (
            <>
              <div className="alert alert-info mb-3">
                <i className="bi bi-info-circle me-2"></i>
                These breeders have requested admin placement. Assign them to available handlers.
              </div>
              
              <div className="table-responsive">
                <table className="table table-hover">
                  <thead>
                    <tr>
                      <th>Breeder</th>
                      <th>Birds</th>
                      <th>Requested</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pendingRotationRequests.map((request) => (
                      <tr key={request.id}>
                        <td>
                          <div>
                            <strong>
                              {request.breederName || request.participantName || 'Unknown Breeder'}
                              {(request.breederLoftName || request.loftName) && (
                                <span className="text-muted"> ({request.breederLoftName || request.loftName})</span>
                              )}
                            </strong>
                            <div className="small text-muted">
                              <i className="bi bi-envelope me-1"></i>
                              {request.breederEmail || request.email || 'No email'}
                            </div>
                            {(request.breederMobile || request.mobile) && (
                              <div className="small text-muted">
                                <i className="bi bi-telephone me-1"></i>
                                {request.breederMobile || request.mobile}
                              </div>
                            )}

                          </div>
                        </td>
                        <td>
                          <span className="badge bg-primary">
                            {request.numberOfBirds} {request.numberOfBirds === 1 ? 'Bird' : 'Birds'}
                          </span>
                          {request.assignedBirds > 0 && (
                            <div className="small text-success mt-1">
                              {request.assignedBirds} assigned, {request.numberOfBirds - request.assignedBirds} remaining
                            </div>
                          )}
                        </td>
                        <td>
                          <small className="text-muted">
                            {new Date(request.requestDate || request.createDate).toLocaleDateString()}
                          </small>
                        </td>
                        <td>
                          <span className="badge bg-warning text-dark">
                            <i className="bi bi-clock me-1"></i>
                            Pending Assignment
                          </span>
                        </td>
                        <td>
                          {isEditing ? (
                            <button 
                              className="btn btn-primary btn-sm"
                              onClick={() => {
                                setSelectedRotationRequest(request);
                                setShowAssignHandlerModal(true);
                              }}
                              disabled={saving}
                            >
                              <i className="bi bi-person-plus me-1"></i>
                              Assign Handler
                            </button>
                          ) : (
                            <span className="text-muted small">
                              Enable edit mode to assign handlers
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {availableHandlers.length === 0 && isEditing && (
                <div className="alert alert-warning mt-3">
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  <strong>No available handlers</strong> - All handlers are at capacity. You may need to increase handler limits or add new handlers.
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Participant Management */}
      <div className="card shadow-sm">
        <div className="card-header bg-white py-3">
          <h5 className="mb-0" style={{ color: '#0b3d91' }}>
            <i className="bi bi-person-gear me-2"></i>
            Participant Management
          </h5>
        </div>
        <div className="card-body">
          {isEditing && (
            <div className="alert alert-warning d-flex align-items-center mb-3">
              <i className="bi bi-pencil-square me-2"></i>
              <strong>Edit Mode Active</strong> - You can edit or add participants
            </div>
          )}

          {isEditing ? (
            <>
              {/* Participant Selection */}
              <div className="mb-3">
                <label className="form-label fw-bold">Select Participant</label>
                <select 
                  className="form-select"
                  value={selectedParticipantId}
                  onChange={(e) => handleParticipantSelect(e.target.value)}
                >
                  <option value="">-- Select a participant to edit --</option>
                  <option value="new">🆕 Add New Participant</option>
                  <optgroup label="Existing Participants">
                    {allParticipants?.sort((a, b) => (a.fullName || '').localeCompare(b.fullName || '')).map(p => (
                      <option key={p.id} value={p.id}>
                        {p.fullName} {p.loftName && `(${p.loftName})`} - {Array.isArray(p.role) ? p.role.join(', ') : p.role}
                      </option>
                    ))}
                  </optgroup>
                </select>
              </div>

              {/* Participant Form */}
              {selectedParticipantId && (
                <div className="border rounded p-3 bg-light">
                  <h6 className="fw-bold mb-3">
                    {isNewParticipant ? (
                      <><i className="bi bi-person-plus me-1"></i>Add New Participant</>
                    ) : (
                      <><i className="bi bi-pencil me-1"></i>Edit: {participantFormData.fullName || 'Loading...'}</>
                    )}
                  </h6>
                  {process.env.NODE_ENV === 'development' && (
                    <details className="mb-3">
                      <summary className="text-muted small" style={{cursor: 'pointer'}}>Debug: Current Form Data</summary>
                      <pre className="small text-muted mt-1" style={{fontSize: '10px'}}>
                        {JSON.stringify(participantFormData, null, 2)}
                      </pre>
                    </details>
                  )}

                  {/* Personal Info */}
                  <div className="row mb-3">
                    <div className="col-md-6">
                      <label className="form-label fw-bold">Full Name <span className="text-danger">*</span></label>
                      <input
                        type="text"
                        className="form-control"
                        value={participantFormData.fullName || ''}
                        onChange={(e) => updateFormField('fullName', e.target.value)}
                        placeholder="Enter full name"
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-bold">Loft Name</label>
                      <input
                        type="text"
                        className="form-control"
                        value={participantFormData.loftName || ''}
                        onChange={(e) => updateFormField('loftName', e.target.value)}
                        placeholder="Enter loft name"
                      />
                    </div>
                  </div>

                  <div className="row mb-3">
                    <div className="col-md-6">
                      <label className="form-label fw-bold">Email <span className="text-danger">*</span></label>
                      <input
                        type="email"
                        className="form-control"
                        value={participantFormData.emailAddress || ''}
                        onChange={(e) => updateFormField('emailAddress', e.target.value)}
                        placeholder="Enter email address"
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-bold">Mobile Phone</label>
                      <input
                        type="tel"
                        className="form-control"
                        value={participantFormData.mobile || ''}
                        onChange={(e) => updateFormField('mobile', e.target.value)}
                        placeholder="Enter mobile number"
                      />
                    </div>
                  </div>

                  {/* Roles */}
                  <div className="mb-3">
                    <label className="form-label fw-bold">Roles</label>
                    <div className="d-flex flex-wrap gap-2">
                      {['breeder', 'handler', 'admin'].map(role => {
                        const isSelected = participantFormData.role?.includes(role.toLowerCase());
                        return (
                          <button
                            key={role}
                            type="button"
                            className={`btn btn-sm ${
                              isSelected 
                                ? (role === 'admin' ? 'btn-danger' : role === 'handler' ? 'btn-success' : 'btn-primary')
                                : 'btn-outline-secondary'
                            }`}
                            onClick={() => {
                              const currentRoles = participantFormData.role || [];
                              if (isSelected) {
                                updateFormField('role', currentRoles.filter(r => r !== role));
                              } else {
                                updateFormField('role', [...currentRoles, role]);
                              }
                            }}
                          >
                            {isSelected && <i className="bi bi-check me-1"></i>}
                            {role.charAt(0).toUpperCase() + role.slice(1)}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Handler Info */}
                  {participantFormData.role?.includes('handler') && (
                    <div className="border rounded p-2 mb-3 bg-white">
                      <h6 className="fw-bold text-success mb-2"><i className="bi bi-hand-thumbs-up me-1"></i>Handler Information</h6>
                      <div className="row mb-2">
                        <div className="col-md-4">
                          <label className="form-label">Section</label>
                          <select
                            className="form-select form-select-sm"
                            value={participantFormData.handlerInfo?.section || ''}
                            onChange={(e) => updateFormField('handlerInfo.section', e.target.value)}
                          >
                            <option value="">Select Section...</option>
                            <option value="NORTH">NORTH</option>
                            <option value="CENTRAL">CENTRAL</option>
                            <option value="SOUTH">SOUTH</option>
                          </select>
                        </div>
                        <div className="col-md-4">
                          <label className="form-label">Distance (miles)</label>
                          <input
                            type="number"
                            className="form-control form-control-sm"
                            value={participantFormData.handlerInfo?.distance || ''}
                            onChange={(e) => {
                              const value = parseFloat(e.target.value) || 0;
                              updateFormField('handlerInfo.distance', value);
                              updateFormField('handlerInfo.distanceFromRaceStation', value);
                            }}
                            min="0"
                            step="0.1"
                            placeholder="Distance from liberation point"
                          />
                        </div>
                        <div className="col-md-4">
                          <label className="form-label">Total Capacity</label>
                          <input
                            type="number"
                            className="form-control form-control-sm"
                            value={participantFormData.handlerInfo?.totalBirds || ''}
                            onChange={(e) => updateFormField('handlerInfo.totalBirds', parseInt(e.target.value) || 0)}
                            min="0"
                          />
                        </div>
                      </div>
                      <div className="row mb-2">
                        <div className="col-md-4">
                          <label className="form-label">Open Slots</label>
                          <input
                            type="number"
                            className="form-control form-control-sm"
                            value={openSlots || ''}
                            onChange={(e) => updateFormField('handlerInfo.openSlots', parseInt(e.target.value) || 0)}
                            min="0"
                          />
                        </div>
                        <div className="col-md-4">
                          <label className="form-label">Breeder Application Method</label>
                          <div className="form-check mt-1">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              id="requireCall"
                              checked={participantFormData.handlerInfo?.breederApplicationMethod?.includes('call') || false}
                              onChange={(e) => {
                                const currentMethods = participantFormData.handlerInfo?.breederApplicationMethod || ['in-app'];
                                const newMethods = e.target.checked 
                                  ? [...currentMethods.filter(m => m !== 'call'), 'call']
                                  : currentMethods.filter(m => m !== 'call');
                                // Ensure in-app is always included
                                const finalMethods = newMethods.includes('in-app') ? newMethods : ['in-app', ...newMethods];
                                updateFormField('handlerInfo.breederApplicationMethod', finalMethods);
                              }}
                            />
                            <label className="form-check-label" htmlFor="requireCall">
                              Require Call (breeders must call first)
                            </label>
                          </div>
                          <small className="text-muted">In-app requests are always enabled</small>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Privacy & Notification Settings */}
                  <div className="border rounded p-2 mb-3 bg-white">
                    <h6 className="fw-bold text-info mb-2"><i className="bi bi-gear me-1"></i>Privacy & Notification Settings</h6>
                    <div className="row">
                      <div className="col-md-6">
                        <label className="form-label">Preferred Notification Method</label>
                        <div className="d-flex flex-wrap gap-2">
                          {['email', 'sms', 'phone'].map(method => {
                            const isSelected = participantFormData.preferredNotificationMethod?.includes(method);
                            return (
                              <button
                                key={method}
                                type="button"
                                className={`btn btn-sm ${isSelected ? 'btn-info' : 'btn-outline-secondary'}`}
                                onClick={() => {
                                  const current = participantFormData.preferredNotificationMethod || [];
                                  if (isSelected) {
                                    updateFormField('preferredNotificationMethod', current.filter(m => m !== method));
                                  } else {
                                    updateFormField('preferredNotificationMethod', [...current, method]);
                                  }
                                }}
                              >
                                {isSelected && <i className="bi bi-check me-1"></i>}
                                <i className={`bi ${method === 'email' ? 'bi-envelope' : method === 'sms' ? 'bi-chat-dots' : 'bi-telephone'} me-1`}></i>
                                {method.charAt(0).toUpperCase() + method.slice(1)}
                              </button>
                            );
                          })}
                        </div>
                        <small className="text-muted">Select one or more notification methods</small>
                      </div>
                      <div className="col-md-6">
                        <label className="form-label">Privacy Settings</label>
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id="hideContactInfo"
                            checked={participantFormData.hideContactInfo || false}
                            onChange={(e) => updateFormField('hideContactInfo', e.target.checked)}
                          />
                          <label className="form-check-label" htmlFor="hideContactInfo">
                            <i className="bi bi-eye-slash me-1"></i>
                            Hide my contact information from other participants
                          </label>
                        </div>
                        <small className="text-muted">When checked, your email and phone won't be visible to other participants</small>
                      </div>
                    </div>
                  </div>

                  {/* Breeder Info */}
                  {participantFormData.role?.includes('breeder') && (
                    <div className="border rounded p-2 mb-3 bg-white">
                      <h6 className="fw-bold text-primary mb-2"><i className="bi bi-egg me-1"></i>Breeder Information</h6>
                      <div className="row">
                        <div className="col-md-6">
                          <label className="form-label">Total Birds for Race</label>
                          <input
                            type="number"
                            className="form-control form-control-sm"
                            value={participantFormData.breederInfo?.totalBirdsForRace || ''}
                            onChange={(e) => updateFormField('breederInfo.totalBirdsForRace', parseInt(e.target.value) || 0)}
                            min="0"
                            placeholder="Number of birds to enter in race"
                          />
                        </div>
                      </div>
                      {!isNewParticipant && (
                        <div className="mt-2">
                          <small className="text-muted">
                            Current Status: {participantFormData.breederInfo?.assignedBirds?.length || 0} birds assigned
                          </small>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Save Button */}
                  <div className="d-flex justify-content-between align-items-center">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => {
                        setSelectedParticipantId('');
                        setIsNewParticipant(false);
                        setParticipantFormData({});
                      }}
                    >
                      Cancel
                    </button>
                    {!isNewParticipant && (
                      <button
                        type="button"
                        className="btn btn-danger me-2"
                        onClick={() => setShowDeleteConfirm(true)}
                        disabled={saving || deletingParticipant}
                      >
                        <i className="bi bi-trash me-1"></i>
                        Delete
                      </button>
                    )}
                    <button
                      type="button"
                      className="btn btn-success"
                      onClick={saveParticipant}
                      disabled={
                        saving ||
                        !participantFormData.fullName ||
                        (!participantFormData.emailAddress && !participantFormData.mobile)
                      }
                    >
                      {saving ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                          Saving...
                        </>
                      ) : (
                        <>
                          <i className="bi bi-check-circle me-1"></i>
                          {isNewParticipant ? 'Add Participant' : 'Save Changes'}
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="text-center text-muted py-4">
              <i className="bi bi-lock display-4 text-muted"></i>
              <p className="mt-2">Enable edit mode to manage participants</p>
            </div>
          )}
        </div>
      </div>

      {/* Admin Assign Handler Modal */}
      {showAssignHandlerModal && selectedRotationRequest && (
        <AdminAssignHandlerModal
          show={showAssignHandlerModal}
          onClose={() => {
            setShowAssignHandlerModal(false);
            setSelectedRotationRequest(null);
          }}
          request={selectedRotationRequest}
          availableHandlers={availableHandlers}
          allRequests={adminHandlersRequestsReceived}
          onAssign={handleAssignHandler}
        />
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header bg-danger text-white">
                <h5 className="modal-title">
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  Confirm Delete Participant
                </h5>
                <button 
                  type="button" 
                  className="btn-close btn-close-white" 
                  onClick={closeDeleteModal}
                  disabled={deletingParticipant && !deleteResult}
                ></button>
              </div>
              <div className="modal-body">
                {deleteResult ? (
                  // Show result after operation
                  <div className={`alert ${deleteResult.success ? 'alert-success' : 'alert-danger'}`}>
                    <i className={`bi ${deleteResult.success ? 'bi-check-circle' : 'bi-exclamation-triangle'} me-2`}></i>
                    <strong>{deleteResult.success ? 'Success!' : 'Error!'}</strong>
                    <p className="mb-0 mt-2">{deleteResult.message}</p>
                  </div>
                ) : (
                  // Show confirmation before operation
                  <>
                    <div className="alert alert-warning">
                      <i className="bi bi-exclamation-triangle me-2"></i>
                      <strong>Warning:</strong> This action cannot be undone!
                    </div>
                    <p>
                      Are you sure you want to permanently delete <strong>{participantFormData.fullName}</strong>?
                    </p>
                    <p className="text-muted mb-0">
                      This will remove all of their information, bird assignments, and handler relationships from the system.
                    </p>
                  </>
                )}
              </div>
              <div className="modal-footer">
                {deleteResult ? (
                  // Show close button after operation
                  <button 
                    type="button" 
                    className="btn btn-primary"
                    onClick={closeDeleteModal}
                  >
                    <i className="bi bi-x-lg me-1"></i>
                    Close
                  </button>
                ) : (
                  // Show cancel/delete buttons before operation
                  <>
                    <button 
                      type="button" 
                      className="btn btn-secondary"
                      onClick={closeDeleteModal}
                      disabled={deletingParticipant}
                    >
                      Cancel
                    </button>
                    <button 
                      type="button" 
                      className="btn btn-danger"
                      onClick={deleteParticipant}
                      disabled={deletingParticipant}
                    >
                      {deletingParticipant ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                          Deleting...
                        </>
                      ) : (
                        <>
                          <i className="bi bi-trash me-1"></i>
                          Yes, Delete Participant
                        </>
                      )}
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
